import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mic2, Youtube, RefreshCw, Plus, Trash2, Eye, Wallet, Loader2, CheckCircle2, Clock, Hourglass, UploadCloud, Film } from "lucide-react";
import { endpoints, apiError } from "@/lib/api";
import { chunkedUpload } from "@/lib/cfUpload";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString();

export default function CreatorStudio() {
  const { isAuthed, ready } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ name: "", bio: "", category: "Music", payoutPhone: "" });
  const [yt, setYt] = useState({ handle: "", channelId: "" });
  const [vid, setVid] = useState({ videoUrl: "", title: "" });
  const [socials, setSocials] = useState({ instagram: "", facebook: "", tiktok: "", twitter: "", music: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [myVods, setMyVods] = useState([]);
  const [vodFile, setVodFile] = useState(null);
  const [vodTitle, setVodTitle] = useState("");
  const [vodPhase, setVodPhase] = useState("");
  const [vodProgress, setVodProgress] = useState(0);

  const load = () => {
    setLoading(true);
    endpoints.creatorMe().then((r) => {
      setMe(r.data?.exists ? r.data : null);
      if (r.data?.exists && r.data.socials) setSocials({ instagram: "", facebook: "", tiktok: "", twitter: "", music: "", website: "", ...r.data.socials });
      if (r.data?.exists) endpoints.creatorMyClaims().then((c) => setClaims(c.data?.claims || [])).catch(() => {});
      if (r.data?.exists) endpoints.creatorMyReport().then((c) => setReport(c.data)).catch(() => {});
      if (r.data?.exists) endpoints.creatorVodMine().then((c) => setMyVods(c.data?.videos || [])).catch(() => {});
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (ready && isAuthed) load(); else if (ready) setLoading(false); }, [ready, isAuthed]);

  const apply = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter your creator name");
    setBusy(true);
    try { await endpoints.creatorApply(form); toast.success("Creator profile created! Awaiting admin approval."); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const connectYt = async () => {
    setBusy(true);
    try { const r = await endpoints.creatorConnectYoutube(yt); toast.success("YouTube channel connected."); if (r.data?.note) toast.message(r.data.note); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sync = async () => {
    setBusy(true);
    try { const r = await endpoints.creatorSync(); if (r.data?.ok) toast.success(`Synced ${r.data.imported} video(s).`); else toast.message(r.data?.note || "Sync unavailable."); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const addVideo = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await endpoints.creatorAddVideo(vid); toast.success("Video added."); setVid({ videoUrl: "", title: "" }); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const delVideo = async (vId) => { await endpoints.creatorDeleteVideo(vId); load(); };
  const uploadVod = async () => {
    if (!vodFile) return toast.error("Choose a video file");
    if (!vodTitle.trim()) return toast.error("Enter a title");
    try {
      setVodPhase("uploading"); setVodProgress(0);
      const { data } = await endpoints.creatorVodDirectInit({ title: vodTitle, access: "free", filename: vodFile.name, contentType: vodFile.type || "video/mp4", size: vodFile.size });
      await chunkedUpload(data.id, vodFile, endpoints.creatorVodDirectChunk, setVodProgress);
      setVodPhase("processing");
      await endpoints.creatorVodDirectFinalize(data.id);
      toast.success("Uploaded! Your video is pending admin approval.");
      setVodFile(null); setVodTitle(""); setVodPhase(""); setVodProgress(0); load();
    } catch (e) { toast.error(apiError(e?.response?.data?.detail) || e.message); setVodPhase(""); }
  };
  const claim = async () => {
    setBusy(true);
    try { await endpoints.creatorClaimReward(); toast.success("Reward request sent! Admin will review and pay out."); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!ready || loading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;

  if (!isAuthed) return (
    <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="studio-signin">
      <Mic2 className="w-14 h-14 text-[#E11D2A] mx-auto mb-4" />
      <h1 className="text-2xl font-bold text-white">Become a B&amp;B KIGALI Creator</h1>
      <p className="text-slate-400 text-sm mt-2">Sign in to create your profile, sync your YouTube content and earn rewards.</p>
      <button onClick={() => window.dispatchEvent(new Event("open-auth"))} data-testid="studio-signin-btn" className="mt-6 h-11 px-7 rounded-full bg-[#E11D2A] text-white font-bold">Sign in</button>
    </div>
  );

  const Card = ({ children, className = "" }) => <div className={`rounded-2xl bg-[#121620] border border-[#273042] p-5 sm:p-6 ${className}`}>{children}</div>;
  const Input = (p) => <input {...p} className="w-full h-11 px-4 rounded-lg bg-[#0A0C10] border border-[#273042] text-white placeholder-slate-500 focus:border-[#E11D2A] outline-none" />;

  if (!me) return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-10" data-testid="studio-apply">
      <h1 className="text-3xl font-extrabold text-white flex items-center gap-2 mb-2"><Mic2 className="w-7 h-7 text-[#E11D2A]" /> Creator Studio</h1>
      <p className="text-slate-400 mb-6">Create your creator profile to get started.</p>
      <Card>
        <form onSubmit={apply} className="space-y-4">
          <div><label className="text-sm text-slate-300">Creator / stage name *</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" placeholder="e.g. DJ Kigali" /></div>
          <div><label className="text-sm text-slate-300">Category</label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="apply-category" placeholder="Music, Comedy, Sports…" /></div>
          <div><label className="text-sm text-slate-300">Short bio</label><textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="apply-bio" rows={3} className="w-full px-4 py-2.5 rounded-lg bg-[#0A0C10] border border-[#273042] text-white placeholder-slate-500 focus:border-[#E11D2A] outline-none" placeholder="Tell fans about you" /></div>
          <div><label className="text-sm text-slate-300">Mobile Money payout number</label><Input value={form.payoutPhone} onChange={(e) => setForm({ ...form, payoutPhone: e.target.value })} data-testid="apply-phone" placeholder="+2507…" /></div>
          <button disabled={busy} type="submit" data-testid="apply-submit" className="w-full h-12 rounded-full bg-[#E11D2A] text-white font-bold disabled:opacity-60">{busy ? "Creating…" : "Create creator profile"}</button>
        </form>
      </Card>
    </div>
  );

  const st = me.stats || {};
  const statusColor = { approved: "text-green-400", pending: "text-amber-400", suspended: "text-red-400", rejected: "text-red-400" }[me.status] || "text-slate-400";
  const activeClaim = claims.find((c) => ["pending", "approved", "processing"].includes(c.status));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="studio-dashboard">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-white flex items-center gap-2">{me.name}</h1>
          <p className={`text-sm font-semibold mt-1 capitalize ${statusColor}`}>Status: {me.status}</p>
        </div>
        {me.status === "approved" && <Link to={`/creators/${me.slug}`} className="h-10 px-5 rounded-full bg-white/5 border border-[#273042] text-white text-sm font-semibold flex items-center">View public profile</Link>}
      </div>

      {me.status !== "approved" && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-amber-300 text-sm flex items-center gap-2" data-testid="studio-pending-note">
          <Clock className="w-4 h-4" /> Your profile is {me.status}. You can add content now; your public page goes live once an admin approves you.
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[["Verified views", fmt(st.totalEligibleViews), Eye],
          ["Videos", fmt(st.videoCount), Youtube],
          ["Reward units earned", fmt(st.earnedUnits), CheckCircle2],
          ["Available to claim", `${fmt(st.availableAmountFrw)} RWF`, Wallet]].map(([label, val, Icon]) => (
          <Card key={label} className="!p-4">
            <Icon className="w-5 h-5 text-[#E11D2A] mb-2" />
            <p className="text-xl font-extrabold text-white">{val}</p>
            <p className="text-xs text-slate-400">{label}</p>
          </Card>
        ))}
      </div>

      <Card>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-white font-semibold">Reward progress</p>
            <p className="text-sm text-slate-400">{fmt(st.viewsPerReward)} verified views = {fmt(st.rewardFrw)} RWF. {st.viewsToNextReward ? `${fmt(st.viewsToNextReward)} views to your next reward.` : ""}</p>
          </div>
          {activeClaim ? (
            <span className="h-11 px-5 rounded-full bg-amber-500/15 text-amber-300 text-sm font-semibold flex items-center gap-2" data-testid="claim-pending"><Hourglass className="w-4 h-4" /> Reward request {activeClaim.status}</span>
          ) : (
            <button onClick={claim} disabled={busy || st.availableUnits < 1 || me.status !== "approved"} data-testid="claim-reward-btn"
              className="h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold disabled:opacity-50 flex items-center gap-2"><Wallet className="w-4 h-4" /> Request My Money</button>
          )}
        </div>
        <div className="mt-3 h-2 rounded-full bg-[#0A0C10] overflow-hidden">
          <div className="h-full bg-[#E11D2A]" style={{ width: `${st.viewsPerReward ? Math.min(100, ((st.totalEligibleViews % st.viewsPerReward) / st.viewsPerReward) * 100) : 0}%` }} />
        </div>
      </Card>

      {/* Earnings & Views report (per-channel + permanent cash-out audit) */}
      {report && (
        <Card data-testid="earnings-report">
          <p className="text-white font-semibold flex items-center gap-2 mb-1"><Eye className="w-5 h-5 text-[#E11D2A]" /> Earnings &amp; Views report</p>
          <p className="text-sm text-slate-400 mb-4">Eligible views per channel, and a permanent record of every cash-out.</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {(report.channels || []).map((ch) => (
              <div key={ch.key} className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3" data-testid={`channel-stat-${ch.key}`}>
                <p className="text-xs text-slate-400">{ch.label}</p>
                <p className="text-lg font-extrabold text-white">{fmt(ch.views)}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3">
              <p className="text-xs text-slate-400">Total eligible views (all-time)</p>
              <p className="text-lg font-extrabold text-white" data-testid="report-total-views">{fmt(report.stats?.totalEligibleViews)}</p>
            </div>
            <div className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3">
              <p className="text-xs text-slate-400">Remaining payable views</p>
              <p className="text-lg font-extrabold text-green-400" data-testid="report-payable-views">{fmt(report.stats?.payableViews)}</p>
            </div>
            <div className="rounded-xl bg-[#0A0C10] border border-[#273042] p-3">
              <p className="text-xs text-slate-400">Already cashed-out views</p>
              <p className="text-lg font-extrabold text-slate-300" data-testid="report-consumed-views">{fmt(report.stats?.consumedViews)}</p>
            </div>
          </div>

          <p className="text-white font-semibold text-sm mb-2">Cash-out history</p>
          {(report.claims || []).length === 0 ? (
            <p className="text-slate-500 text-sm" data-testid="cashout-empty">No cash-outs yet. Your first reward will appear here with full details.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#273042]" data-testid="cashout-history">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0C10] text-slate-400">
                  <tr>
                    <th className="text-left py-2.5 px-3">Requested</th>
                    <th className="text-left py-2.5 px-2">Paid</th>
                    <th className="py-2.5 px-2">Views paid</th>
                    <th className="py-2.5 px-2">Amount</th>
                    <th className="py-2.5 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.claims.map((cl) => (
                    <tr key={cl.id} className="border-t border-[#273042] text-slate-200" data-testid={`cashout-row-${cl.id}`}>
                      <td className="py-2.5 px-3 whitespace-nowrap">{cl.createdAt ? new Date(cl.createdAt).toLocaleString() : "—"}</td>
                      <td className="py-2.5 px-2 whitespace-nowrap">{cl.paidAt ? new Date(cl.paidAt).toLocaleString() : "—"}</td>
                      <td className="text-center">{fmt(cl.viewsIncluded || (cl.rewardUnits || 0) * (cl.viewsPerReward || 0))}</td>
                      <td className="text-center font-semibold text-white">{fmt(cl.amountFrw)} RWF</td>
                      <td className="text-center capitalize"><span className={cl.status === "paid" ? "text-green-400" : cl.status === "rejected" || cl.status === "failed" ? "text-red-400" : "text-amber-400"}>{cl.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* YouTube connect + sync */}
      <Card>
        <p className="text-white font-semibold flex items-center gap-2 mb-3"><Youtube className="w-5 h-5 text-[#E11D2A]" /> YouTube channel</p>
        {me.youtubeChannelId ? <p className="text-sm text-green-400 mb-3">Connected: {me.youtubeHandle || me.youtubeChannelId}</p> : <p className="text-sm text-slate-400 mb-3">Connect your channel to sync your uploads automatically.</p>}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={yt.handle} onChange={(e) => setYt({ ...yt, handle: e.target.value })} data-testid="yt-handle" placeholder="@yourhandle" />
          <Input value={yt.channelId} onChange={(e) => setYt({ ...yt, channelId: e.target.value })} data-testid="yt-channel" placeholder="or UC… channel ID" />
          <button onClick={connectYt} disabled={busy} data-testid="yt-connect-btn" className="h-11 px-5 rounded-lg bg-white/5 border border-[#273042] text-white text-sm font-semibold shrink-0">Connect</button>
          <button onClick={sync} disabled={busy} data-testid="yt-sync-btn" className="h-11 px-5 rounded-lg bg-[#E11D2A] text-white text-sm font-semibold shrink-0 flex items-center gap-1.5"><RefreshCw className="w-4 h-4" /> Sync</button>
        </div>
      </Card>

      {/* Social channels hub */}
      <Card>
        <p className="text-white font-semibold flex items-center gap-2 mb-1"><Youtube className="w-5 h-5 text-[#E11D2A]" /> Your social channels</p>
        <p className="text-sm text-slate-400 mb-3">Add your official links — fans see them all on your B&B KIGALI profile. Leave blank what you don't have.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {[["instagram","Instagram"],["facebook","Facebook"],["tiktok","TikTok"],["twitter","X / Twitter"],["music","Music"],["website","Website"]].map(([k,label]) => (
            <Input key={k} value={socials[k]} onChange={(e) => setSocials({ ...socials, [k]: e.target.value })} data-testid={`social-${k}`} placeholder={`${label} link`} />
          ))}
        </div>
        <button onClick={async () => { setBusy(true); try { await endpoints.creatorUpdateMe({ socials }); toast.success("Social channels saved"); load(); } catch (e) { toast.error("Could not save"); } finally { setBusy(false); } }}
          disabled={busy} data-testid="socials-save-btn" className="mt-3 h-11 px-6 rounded-lg bg-[#E11D2A] text-white text-sm font-semibold">Save channels</button>
      </Card>

      {/* Add video manually */}
      <Card>
        <p className="text-white font-semibold flex items-center gap-2 mb-3"><Plus className="w-5 h-5 text-[#E11D2A]" /> Add a video</p>
        <form onSubmit={addVideo} className="flex flex-col sm:flex-row gap-2">
          <Input value={vid.videoUrl} onChange={(e) => setVid({ ...vid, videoUrl: e.target.value })} data-testid="video-url" placeholder="YouTube link or video ID" />
          <Input value={vid.title} onChange={(e) => setVid({ ...vid, title: e.target.value })} data-testid="video-title" placeholder="Title (optional)" />
          <button type="submit" disabled={busy} data-testid="video-add-btn" className="h-11 px-6 rounded-lg bg-[#E11D2A] text-white text-sm font-semibold shrink-0">Add</button>
        </form>
      </Card>

      {/* Upload HD video to Cloudflare (pending admin approval) */}
      <Card>
        <p className="text-white font-semibold flex items-center gap-2 mb-1"><UploadCloud className="w-5 h-5 text-[#E11D2A]" /> Upload HD video</p>
        <p className="text-xs text-slate-400 mb-3">Upload from your device — streamed in full HD. New uploads go to admin for approval before going live.</p>
        <input type="file" accept="video/*" onChange={(e) => setVodFile(e.target.files?.[0] || null)} data-testid="creator-vod-file"
          className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#E11D2A] file:text-white file:font-semibold mb-2" />
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={vodTitle} onChange={(e) => setVodTitle(e.target.value)} data-testid="creator-vod-title" placeholder="Video title" />
          <button onClick={uploadVod} disabled={!!vodPhase} data-testid="creator-vod-upload-btn" className="h-11 px-5 rounded-lg bg-[#E11D2A] text-white text-sm font-semibold shrink-0 flex items-center gap-1.5 disabled:opacity-60">
            {vodPhase ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {vodPhase === "uploading" ? `${vodProgress}%` : vodPhase === "processing" ? "Processing…" : "Upload"}
          </button>
        </div>
        {myVods.length > 0 && (
          <div className="mt-4 space-y-2">
            {myVods.map((mv) => (
              <div key={mv.id} className="flex items-center gap-2 text-sm py-2 border-b border-[#273042] last:border-0" data-testid={`creator-vod-${mv.id}`}>
                <Film className="w-4 h-4 text-slate-500 shrink-0" />
                <span className="text-slate-200 flex-1 truncate">{mv.title}</span>
                <span className="text-xs capitalize text-slate-400">{mv.status === "ready" ? mv.approval : mv.status}</span>
                <button onClick={async () => { await endpoints.creatorVodDelete(mv.id); load(); }} className="text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Claims history */}
    </div>
  );
}
